"use client"

import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { useTranslations } from "@/components/i18n/use-translations";

const classifyIpv4 = (value: string) => {
    const octets = value.split(".");
    if (octets.length !== 4) return null;

    const numbers = octets.map((part) => Number(part));
    if (numbers.some((num) => Number.isNaN(num) || num < 0 || num > 255)) {
        return null;
    }

    const [a, b] = numbers;

    if (a === 10) return "private10";
    if (a === 172 && b >= 16 && b <= 31) return "private172";
    if (a === 192 && b === 168) return "private192";
    if (a === 127) return "loopbackIpv4";
    if (a === 169 && b === 254) return "linkLocalIpv4";
    if (a >= 224 && a <= 239) return "multicastIpv4";
    if (a >= 240) return "reserved240";
    if (a === 0) return "reserved0";

    return "public";
};

const ipv6Regex = /^(?:[A-Fa-f0-9]{1,4}:){7}[A-Fa-f0-9]{1,4}$|^(?:[A-Fa-f0-9]{1,4}:){1,7}:$|^:(?::[A-Fa-f0-9]{1,4}){1,7}$|^(?:[A-Fa-f0-9]{1,4}:){1,6}:[A-Fa-f0-9]{1,4}$|^(?:[A-Fa-f0-9]{1,4}:){1,5}(?::[A-Fa-f0-9]{1,4}){1,2}$|^(?:[A-Fa-f0-9]{1,4}:){1,4}(?::[A-Fa-f0-9]{1,4}){1,3}$|^(?:[A-Fa-f0-9]{1,4}:){1,3}(?::[A-Fa-f0-9]{1,4}){1,4}$|^(?:[A-Fa-f0-9]{1,4}:){1,2}(?::[A-Fa-f0-9]{1,4}){1,5}$|^[A-Fa-f0-9]{1,4}:(?:(?::[A-Fa-f0-9]{1,4}){1,6})$|^:(?::[A-Fa-f0-9]{1,4}){1,7}$/;

const classifyIpv6 = (value: string) => {
    if (!ipv6Regex.test(value)) return null;

    const lower = value.toLowerCase();

    if (lower === "::" || lower === "::0") return "unspecified";
    if (lower === "::1" || lower.endsWith(":0:0:0:0:0:0:1")) return "loopbackIpv6";
    if (lower.startsWith("fe8") || lower.startsWith("fe9") || lower.startsWith("fea") || lower.startsWith("feb")) {
        return "linkLocalIpv6";
    }
    if (lower.startsWith("fc") || lower.startsWith("fd")) return "uniqueLocal";
    if (lower.startsWith("ff")) return "multicastIpv6";
    if (lower.startsWith("2001:db8")) return "documentationIpv6";

    return "globalUnicast";
};

const summarizeIp = (value: string) => {
    if (!value) return null;

    if (value.includes(".")) {
        const verdict = classifyIpv4(value);
        if (!verdict) return null;

        return { version: "IPv4", verdict };
    }

    if (value.includes(":")) {
        const verdict = classifyIpv6(value);
        if (!verdict) return null;

        return { version: "IPv6", verdict };
    }

    return null;
};

export function IpInspector() {
    const [value, setValue] = useState("8.8.8.8");
    const { messages } = useTranslations();

    const summary = useMemo(() => summarizeIp(value.trim()), [value]);
    const verdictLabels = messages.tools.ipInspector.verdicts;

    return (
        <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-lg shadow-cyan-500/10">
                <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{messages.tools.ipInspector.label}</p>
                    <h1 className="text-2xl font-semibold text-white">{messages.tools.ipInspector.title}</h1>
                    <p className="text-sm text-gray-400">{messages.tools.ipInspector.description}</p>
                </div>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                    <Input
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                        placeholder={messages.tools.ipInspector.placeholder}
                        aria-label={messages.tools.ipInspector.placeholder}
                        className="flex-1 h-12 border border-slate-800 bg-transparent text-slate-100 placeholder:text-gray-500 ring-2 ring-cyan-500/25 focus:ring-cyan-500/60"
                    />
                    <div className="flex gap-2 text-xs">
                        <button
                            type="button"
                            onClick={() => setValue("8.8.8.8")}
                            className="h-10 rounded-full bg-linear-to-r from-cyan-500 to-blue-600 px-4 text-xs font-semibold text-white shadow-md shadow-cyan-500/25 transition hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/35"
                        >
                            {messages.tools.ipInspector.ipv4Example}
                        </button>
                        <button
                            type="button"
                            onClick={() => setValue("2001:4860:4860::8888")}
                            className="h-10 rounded-full bg-linear-to-r from-cyan-500 to-blue-600 px-4 text-xs font-semibold text-white shadow-md shadow-cyan-500/25 transition hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/35"
                        >
                            {messages.tools.ipInspector.ipv6Example}
                        </button>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
                {summary ? (
                    <div className="space-y-3">
                        <p className="text-sm text-gray-400">{messages.tools.ipInspector.classification}</p>
                        <p className="text-2xl font-semibold text-white">{verdictLabels[summary.verdict as keyof typeof verdictLabels]}</p>
                        <p className="text-sm text-gray-400">
                            {summary.verdict === "public" || summary.verdict === "globalUnicast"
                                ? messages.tools.ipInspector.notes.public
                                : messages.tools.ipInspector.notes.nonPublic}
                        </p>
                    </div>
                ) : (
                    <p className="text-sm text-rose-200">{messages.tools.ipInspector.invalid}</p>
                )}
            </div>
        </div>
    );
}
